import {
  MEMBER_GENDER_IDENTITY_OPTIONS,
  MEMBER_PRONOUN_OPTIONS,
  MEMBER_PROFILE_IDENTITY_TEXT_MAX,
} from '@galapagos/shared';

/** The extra option that opens a free-text field instead of storing a preset. */
export const SELF_DESCRIBE = 'selfDescribe';

export interface GenderInfoOption {
  value: string;
  labelKey: string;
}

// Full catalog keys, written out, so the locale dead-key scan can see each one.
const IDENTITY_LABEL_KEYS = {
  none: 'birthDate.genderIdentityOptions.none',
  cisWoman: 'birthDate.genderIdentityOptions.cisWoman',
  cisMan: 'birthDate.genderIdentityOptions.cisMan',
  transwoman: 'birthDate.genderIdentityOptions.transwoman',
  transman: 'birthDate.genderIdentityOptions.transman',
  nonBinary: 'birthDate.genderIdentityOptions.nonBinary',
  genderqueer: 'birthDate.genderIdentityOptions.genderqueer',
} as const satisfies Record<(typeof MEMBER_GENDER_IDENTITY_OPTIONS)[number], string>;

const PRONOUN_LABEL_KEYS = {
  none: 'birthDate.pronounOptions.none',
  theyThem: 'birthDate.pronounOptions.theyThem',
  sheHer: 'birthDate.pronounOptions.sheHer',
  heHim: 'birthDate.pronounOptions.heHim',
  zeZir: 'birthDate.pronounOptions.zeZir',
} as const satisfies Record<(typeof MEMBER_PRONOUN_OPTIONS)[number], string>;

/** The identity dropdown's options, in production's order, Self Describe last. */
export const GENDER_IDENTITY_OPTIONS: readonly GenderInfoOption[] = [
  ...MEMBER_GENDER_IDENTITY_OPTIONS.map((value) => ({
    value,
    labelKey: IDENTITY_LABEL_KEYS[value],
  })),
  { value: SELF_DESCRIBE, labelKey: 'birthDate.genderIdentityOptions.selfDescribe' },
];

/** The pronouns dropdown's options, in production's order, Self Describe last. */
export const PRONOUN_OPTIONS: readonly GenderInfoOption[] = [
  ...MEMBER_PRONOUN_OPTIONS.map((value) => ({ value, labelKey: PRONOUN_LABEL_KEYS[value] })),
  { value: SELF_DESCRIBE, labelKey: 'birthDate.pronounOptions.selfDescribe' },
];

/** What one dropdown shows: the picked option (empty when none) and any self-described words. */
export interface GenderInfoSelection {
  choice: string;
  text: string;
}

export const NO_SELECTION: GenderInfoSelection = { choice: '', text: '' };

/**
 * The dropdown state for a stored value: a preset key picks that option, any
 * other text is the member's own words under Self Describe, and nothing picks
 * nothing.
 */
export function selectionFromStored(
  stored: string | null,
  presets: readonly string[],
): GenderInfoSelection {
  if (stored === null) return NO_SELECTION;
  if (presets.includes(stored)) return { choice: stored, text: '' };
  return { choice: SELF_DESCRIBE, text: stored };
}

/** The value to keep for a dropdown: the preset key, the trimmed words, or null when there is none. */
export function storedFromSelection(selection: GenderInfoSelection): string | null {
  if (selection.choice === '') return null;
  if (selection.choice !== SELF_DESCRIBE) return selection.choice;
  const words = selection.text.trim();
  return words === '' ? null : words;
}

/**
 * Self-described words, cut to what the profile save accepts. The save counts
 * UTF-16 units (an emoji is two), which is never more than the database's
 * per-character count, so this bound satisfies both. The cut never splits a
 * character in two.
 */
export function capIdentityText(text: string): string {
  if (text.length <= MEMBER_PROFILE_IDENTITY_TEXT_MAX) return text;
  let kept = '';
  for (const character of text) {
    if (kept.length + character.length > MEMBER_PROFILE_IDENTITY_TEXT_MAX) break;
    kept += character;
  }
  return kept;
}
